import {
  isOfferCreatedAtEligibleForCutoff,
  isSimplifiedPresentationMarketEligible,
} from "./simplifiedPresentationEligibility";
import type { SelfSupplyEligibleMarket } from "./selfSupplyEligibility";

/** First failing check wins; null reason when show is true. */
type SimplifiedExperienceIneligibleReason =
  | "FEATURE_FLAG_OFF"
  | "MARKET_NOT_TARGETED"
  | "CAP_PAGE_LOADING"
  | "FLAGS_NOT_INITIALISED"
  | "FLAGS_LOADING"
  | "MISSING_OFFER_CREATED_AT"
  | "OFFER_CREATED_BEFORE_CUTOFF"
  | "MISSING_OFFER"
  | "SECOND_SYSTEM"
  | "MISSING_SELF_SUPPLY"
  | "CHANGE_ORDER_MISSING_ORIGINAL_SELF_SUPPLY"
  | "NON_SOLAR_OR_STORAGE_ONLY"
  | "NOT_SOLAR_AND_BATTERY_PACKAGE"
  | "OFFER_PRODUCT_INELIGIBLE";

type SimplifiedExperienceDecision = {
  show: boolean;
  reason: SimplifiedExperienceIneligibleReason | null;
};

const simplifiedExperienceEligible = (): SimplifiedExperienceDecision => ({
  show: true,
  reason: null,
});

const simplifiedExperienceIneligible = (
  reason: SimplifiedExperienceIneligibleReason
): SimplifiedExperienceDecision => ({
  show: false,
  reason,
});

type EvaluateSimplifiedPresentationGateArgs = {
  offerReviewSimplifiedEnabled: boolean;
  presentationTargets: SelfSupplyEligibleMarket[];
  state: string | undefined;
  utility: string | undefined;
  createdDateCutoff?: string | undefined;
  subjectCreatedAt?: string | undefined;
};

const evaluateSimplifiedPresentationGate = ({
  offerReviewSimplifiedEnabled,
  presentationTargets,
  state,
  utility,
  createdDateCutoff,
  subjectCreatedAt,
}: EvaluateSimplifiedPresentationGateArgs): SimplifiedExperienceDecision => {
  if (!offerReviewSimplifiedEnabled) {
    return simplifiedExperienceIneligible("FEATURE_FLAG_OFF");
  }

  if (
    !isSimplifiedPresentationMarketEligible(state, utility, presentationTargets)
  ) {
    return simplifiedExperienceIneligible("MARKET_NOT_TARGETED");
  }

  if (createdDateCutoff) {
    if (!subjectCreatedAt?.trim()) {
      return simplifiedExperienceIneligible("MISSING_OFFER_CREATED_AT");
    }

    if (
      !isOfferCreatedAtEligibleForCutoff(subjectCreatedAt, createdDateCutoff)
    ) {
      return simplifiedExperienceIneligible("OFFER_CREATED_BEFORE_CUTOFF");
    }
  }

  return simplifiedExperienceEligible();
};

export {
  evaluateSimplifiedPresentationGate,
  simplifiedExperienceEligible,
  simplifiedExperienceIneligible,
};
export type {
  EvaluateSimplifiedPresentationGateArgs,
  SimplifiedExperienceDecision,
  SimplifiedExperienceIneligibleReason,
};
